import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { addExpenseActiveListener, addExpenseShortcutListener, clearQuickExpensePath, consumeExpenseShortcut, isNativeExpenseAvailable, pendingQuickExpensePath, rememberQuickExpensePath } from '../services/nativeExpenseService';
import { syncQuickExpenses } from '../services/quickExpenseService';

export default function QuickExpenseBridge() {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const canCreate = hasPermission('expenseRequests', 'create');
  const current = useRef({ user, canCreate, navigate });
  current.current = { user, canCreate, navigate };
  useEffect(() => {
    let disposed = false; const listeners = [];
    const sync = () => { const session = current.current; if (session.user?.id && session.canCreate && !document.hidden) syncQuickExpenses(session.user.id).catch((error) => console.warn('Quick expense sync:', error.message)); };
    const consume = async () => {
      try {
        const { action } = await consumeExpenseShortcut();
        if (!['new', 'camera'].includes(action)) return;
        const path = `/expense-tickets/quick${action === 'camera' ? '?capture=1' : ''}`;
        rememberQuickExpensePath(path);
        if (disposed) return;
        const session = current.current;
        session.navigate(session.user ? path : '/login', { state: { quickExpenseNew: true } });
        if (session.user) clearQuickExpensePath();
      } catch (error) { console.warn('Quick expense shortcut:', error.message); }
    };
    const register = async () => {
      if (!isNativeExpenseAvailable()) return;
      for (const promise of [addExpenseShortcutListener(consume), addExpenseActiveListener(() => { sync(); consume(); })]) {
        const listener = await promise;
        if (disposed) listener.remove(); else listeners.push(listener);
      }
      if (!disposed) consume();
    };
    register().catch((error) => console.warn('Quick expense native registration:', error.message));
    window.addEventListener('online', sync); window.addEventListener('focus', sync); document.addEventListener('visibilitychange', sync);
    sync();
    return () => { disposed = true; listeners.forEach((listener) => listener.remove()); window.removeEventListener('online', sync); window.removeEventListener('focus', sync); document.removeEventListener('visibilitychange', sync); };
  }, []);
  useEffect(() => {
    if (!user?.id) return;
    const path = pendingQuickExpensePath();
    if (path !== '/dashboard') { clearQuickExpensePath(); navigate(path, { state: { quickExpenseNew: true } }); }
    if (canCreate && !document.hidden) syncQuickExpenses(user.id).catch((error) => console.warn('Quick expense sync:', error.message));
  }, [user?.id, canCreate, navigate]);
  return null;
}
